import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { GruppeStandort } from './gruppe-standort';
import { HttpParams } from '@angular/common/http';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { MessageService } from '../../../services/message-service/message.service';
import { GruppenService } from '../gruppen.service';
import { KieneUtils } from '../../../utils/KieneUtils';
import { Gruppe } from '../classes/gruppe';
import { MatDialog } from '@angular/material/dialog';
import { GruppeStandortWechselnDialogComponent } from '../gruppe-standort-wechseln-dialog/gruppe-standort-wechseln-dialog.component';
import { GruppeStandortWechselnDialogResult } from '../gruppe-standort-wechseln-dialog/gruppe-standort-wechseln-dialog-result';
import { TierFilterSettings } from '../../gruppe-tier/filter/gruppe-tier-filter-dialog/classes/tier-filter-settings';
import { TierFilterDialogResult } from '../../gruppe-tier/filter/gruppe-tier-filter-dialog/classes/tier-filter-dialog-result';
import { GruppeTierGewichtPreisDialogInput } from '../../gruppe-tier/gruppe-tier-gewicht-preis-dialog/gruppe-tier-gewicht-preis-dialog';
import { GruppeTierGewichtPreisDialogComponent } from '../../gruppe-tier/gruppe-tier-gewicht-preis-dialog/gruppe-tier-gewicht-preis-dialog.component';
import { GruppeTierFilterDialogComponent } from '../../gruppe-tier/filter/gruppe-tier-filter-dialog/gruppe-tier-filter-dialog.component';
import { EinzeltiergewichtDialogComponent } from './einzeltiergewicht-dialog/einzeltiergewicht-dialog.component';
import { ConfirmDialogInput } from '../../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { AppService } from '../../../services/app/app.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { StepperOrientation } from '@angular/cdk/stepper';

@Component({
    selector: 'kiene-gruppe-standort-verwalten',
    templateUrl: './gruppe-standort-verwalten.component.html',
    styleUrls: ['./gruppe-standort-verwalten.component.scss'],
})
export class GruppeStandortVerwaltenComponent implements OnInit, OnDestroy, OnChanges {
    private readonly editRoleIds = [5, 20];
    standorte: GruppeStandort[] = [];
    standorteSubscription: Subscription;
    reloadStandorteSubscription: Subscription;

    loadingOrSaving = false;
    canEditStandorte = false;
    expandedStandortIndex: number | null = 0;

    stepperOrientation: StepperOrientation = 'vertical';

    @Input('gruppe') gruppe: Gruppe;
    @Input('filterSettings') filterSettings: TierFilterSettings;

    constructor(
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
        private messageService: MessageService,
        private gruppenService: GruppenService,
        private dialog: MatDialog,
        public appService: AppService,
        private localStorageService: LocalStorageService,
    ) {}

    loadStandorte() {
        this.loadingOrSaving = true;
        this.standorteSubscription?.unsubscribe();
        let params = new HttpParams().set('gruppe_id', this.gruppe?.gruppe_id?.toString());
        params = params.set('orderby', 'datum_von');
        if (this.localStorageService.hasPermission(9161)) {
            params = params.set('gewichte', '1');
        }
        if (this.localStorageService.hasPermission(9162)) {
            params = params.set('preise', '1');
        }
        this.standorteSubscription = this.api.getAll(this.apiBaseUrl + 'gruppe_standort/read.php', params).subscribe({
            next: response => {
                this.standorte = response.records;
                if (this.standorte.length === 0) {
                    this.expandedStandortIndex = null;
                } else if (
                    this.expandedStandortIndex === null ||
                    this.expandedStandortIndex < 0 ||
                    this.expandedStandortIndex >= this.standorte.length
                ) {
                    this.expandedStandortIndex = 0;
                }
                this.loadingOrSaving = false;
            },
            error: err => {
                this.messageService.errorMessage('Standorte konnten nicht geladen werden!', err);
                this.loadingOrSaving = false;
            },
        });
    }

    editStandort(standort: GruppeStandort, index: number) {
        if (!this.canEditStandorte) {
            return;
        }
        const dialogRefStandortBearbeiten = this.dialog.open(GruppeStandortWechselnDialogComponent, {
            data: { gruppe: this.gruppe, gruppeStandort: standort, showGewicht: index !== 0 },
        });
        dialogRefStandortBearbeiten.afterClosed().subscribe({
            next: () => {
                this.ladeAlles();
            },
        });
    }

    deleteStandort(gruppeStandort: GruppeStandort) {
        if (!this.canEditStandorte) {
            return;
        }
        const confirmInputData = new ConfirmDialogInput();
        confirmInputData.headline = 'Standortwechsel wirklich löschen?';
        confirmInputData.text = 'Wollen Sie den Standortwechsel wirklich löschen?';
        confirmInputData.okButton = 'Löschen';
        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmInputData });
        dialogRef.afterClosed().subscribe({
            next: result => {
                if (result?.ok && result?.confirmed) {
                    this.loadingOrSaving = true;
                    this.api.post(this.apiBaseUrl + 'gruppe_standort/delete.php', gruppeStandort).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Der Standortwechsel wurde erfolgreich gelöscht!');
                            this.ladeAlles(); // loadingOrSaving
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            this.loadingOrSaving = false;
                        },
                    });
                }
            },
        });
    }

    standortwechsel() {
        if (!this.canUseStandortwechsel() || this.gruppe?.historie > 0) {
            return;
        }
        this.loadAlleTiereFuerStandortwechsel();
    }

    canUseStandortwechsel(): boolean {
        return this.localStorageService.hasPermission(9352);
    }

    private loadAlleTiereFuerStandortwechsel(): void {
        const pageSize = 1000;
        const allRows: { gt_id: number; ohrmarke_notnull: string }[] = [];

        const loadPage = (offset: number) => {
            let params = new HttpParams().set('gruppe_id', this.gruppe?.gruppe_id?.toString());
            params = params.set('limit', pageSize.toString());
            params = params.set('offset', offset.toString());
            params = params.set('standort_wechsel_aktiv', '1');

            this.api.get(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', params).subscribe({
                next: response => {
                    const rows = response?.records || [];
                    for (const t of rows) {
                        const gtId = Number(t?.gt_id);
                        if (
                            gtId > 0 &&
                            GruppeStandortWechselnDialogComponent.isTierAktivForStandortwechsel(t)
                        ) {
                            allRows.push({
                                gt_id: gtId,
                                ohrmarke_notnull: t?.ohrmarke_notnull,
                            });
                        }
                    }

                    const totalCount = Number(response?.count);
                    const loadedCount = allRows.length;
                    if (!isNaN(totalCount) && loadedCount < totalCount && rows.length > 0) {
                        loadPage(offset + pageSize);
                        return;
                    }

                    this.openStandortwechselDialog(allRows);
                },
                error: err => {
                    this.messageService.errorMessage('Tiere fuer Standortwechsel konnten nicht geladen werden!', err);
                    this.openStandortwechselDialog([]);
                },
            });
        };

        loadPage(0);
    }

    private openStandortwechselDialog(selectedTiere: { gt_id: number; ohrmarke_notnull: string }[]) {
        this.dialog
            .open(GruppeStandortWechselnDialogComponent, { data: { gruppe: this.gruppe, selectedTiere: selectedTiere } })
            .afterClosed()
            .subscribe({
                next: (result: GruppeStandortWechselnDialogResult) => {
                    if (result && result.confirm) {
                        this.ladeAlles();
                    }
                },
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.gruppe) {
            this.loadStandorte();
        }
    }

    ngOnDestroy(): void {
        this.standorteSubscription?.unsubscribe();
        this.reloadStandorteSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.canEditStandorte = this.currentUserCanEditStandorte();
        if (!this.appService.isMobileScreen) {
            this.stepperOrientation = 'horizontal';
        }
        this.reloadStandorteSubscription = this.gruppenService.watchReloadStandorte().subscribe({
            next: () => {
                this.loadStandorte();
            },
        });
    }

    public ladeAlles() {
        this.gruppenService.reloadGruppe();
        this.gruppenService.reloadVerlauf();
        this.gruppenService.reloadGewichteUndPreise();
        this.loadStandorte();
    }

    gewichtePreiseEingebenEinstallen(gruppeStandort: GruppeStandort, tfs?: TierFilterSettings) {
        if (!this.canEditStandorte) {
            return;
        }
        if (!tfs) {
            tfs = this.filterSettings;
        }

        if (tfs) {
            tfs.showBetriebskontakte = true;
            tfs.showViehhaendler = true;
        }

        const hatViehhaendler = tfs?.viehhaendler?.length === 1;
        const hatBetriebskontakt = tfs?.betriebskontakte?.length === 1;

        if (tfs?.einstalldatum?.length >= 1) {
            const input = new GruppeTierGewichtPreisDialogInput();
            input.einstallen = true;
            input.gruppeStandort = gruppeStandort;
            if (hatViehhaendler) {
                input.viehhaendler = tfs.viehhaendler[0];
            } else if (hatBetriebskontakt) {
                input.betriebskontakt = tfs.betriebskontakte[0];
            }
            for (const ed of tfs.einstalldatum) {
                input.einstalldatums.push(ed.einstalldatum);
            }
            const dialogRef = this.dialog.open(GruppeTierGewichtPreisDialogComponent, { data: input });
            dialogRef.afterClosed().subscribe({
                next: (result: boolean) => {
                    if (result === true) {
                        this.filterSettings = null;
                        this.ladeAlles();
                    }
                },
            });
        } else {
            const tfsNeu = new TierFilterSettings();
            tfsNeu.showAbgangslisten = false;
            tfsNeu.showAbteile = false;
            tfsNeu.showFilter = false;
            tfsNeu.showGeschlechter = false;
            tfsNeu.showRassen = false;
            tfsNeu.showReklamationen = false;
            tfsNeu.showViehhaendler = true;
            tfsNeu.showBetriebskontakte = true;
            tfsNeu.showHerkunftsbetriebe = false;

            const dialogRef = this.dialog.open(GruppeTierFilterDialogComponent, {
                width: '90vw',
                data: {
                    gruppe: this.gruppe,
                    filterSettings: tfsNeu,
                    info: 'Bitte mindestens ein Einstalldatum wählen (Händler/Kontakt optional).',
                },
            });
            dialogRef.afterClosed().subscribe({
                next: (res: TierFilterDialogResult | TierFilterSettings) => {
                    const settings = TierFilterDialogResult.unwrap(res);
                    if (settings) {
                        this.gewichtePreiseEingebenEinstallen(gruppeStandort, settings);
                    }
                },
            });
        }
    }

    gewichtePreiseEingebenAusstallen(gruppeStandort: GruppeStandort, tfs?: TierFilterSettings) {
        if (!this.canEditStandorte) {
            return;
        }
        if (!tfs) {
            tfs = this.filterSettings;
        }

        if (tfs?.abgangslisten?.length === 1) {
            const input = new GruppeTierGewichtPreisDialogInput();
            input.ausstallen = true;
            input.gruppeStandort = gruppeStandort;
            input.tierliste = tfs.abgangslisten[0];
            const dialogRef = this.dialog.open(GruppeTierGewichtPreisDialogComponent, { data: input });
            dialogRef.afterClosed().subscribe({
                next: (result: boolean) => {
                    if (result === true) {
                        this.filterSettings = undefined;
                        this.ladeAlles();
                    }
                },
            });
        } else {
            const tfsNeu = new TierFilterSettings();
            tfsNeu.showAbteile = false;
            tfsNeu.showFilter = false;
            tfsNeu.showGeschlechter = false;
            tfsNeu.showRassen = false;
            tfsNeu.showReklamationen = false;
            tfsNeu.showEinstalldatum = false;
            tfsNeu.showViehhaendler = false;
            tfsNeu.showHerkunftsbetriebe = false;

            const dialogRef = this.dialog.open(GruppeTierFilterDialogComponent, {
                width: '90vw',
                data: { gruppe: this.gruppe, filterSettings: tfsNeu, info: 'Bitte eine Abgangsliste auswählen!' },
            });
            dialogRef.afterClosed().subscribe({
                next: (res: TierFilterDialogResult | TierFilterSettings) => {
                    const settings = TierFilterDialogResult.unwrap(res);
                    if (settings) {
                        this.gewichtePreiseEingebenAusstallen(gruppeStandort, settings);
                    }
                },
            });
        }
    }

    einzeltiergewichtEintragen(s: GruppeStandort) {
        if (!this.canEditStandorte) {
            return;
        }
        const dialogRef = this.dialog.open(EinzeltiergewichtDialogComponent, {
            data: { gruppe: this.gruppe, gruppeStandort: s },
            minWidth: this.appService.isMobileScreen ? '100vw' : '400px',
            width: '100%',
            minHeight: this.appService.isMobileScreen ? '90vh' : '',
        });
        dialogRef.afterClosed().subscribe({
            next: () => {
                this.ladeAlles();
            },
        });
    }

    private currentUserCanEditStandorte(): boolean {
        const roles = this.localStorageService.getCurrentUser()?.benutzerrollen;
        if (!roles || roles.length === 0) {
            return false;
        }
        return roles.some(role => KieneUtils.includesId(this.editRoleIds, role?.benutzerrolle_id));
    }

    getStandortTierCount(standort: GruppeStandort, index?: number): number {
        const gstCount = Number(standort?.gst_tier_anzahl);
        if (!isNaN(gstCount) && gstCount > 0) {
            return gstCount;
        }

        const directCount = Number(standort?.anzahl_tiere);
        if (!isNaN(directCount) && directCount > 0) {
            return directCount;
        }

        const einstallCount = Number(standort?.einstallgewicht_avg?.anzahl);
        const abgangCount = Number(standort?.abgangsgewicht_avg?.anzahl);

        if (index === 0) {
            const linkedCount = Math.max(
                !isNaN(einstallCount) && einstallCount > 0 ? einstallCount : 0,
                !isNaN(abgangCount) && abgangCount > 0 ? abgangCount : 0,
            );
            if (linkedCount > 0) {
                return linkedCount;
            }
        }

        if (!isNaN(einstallCount) && einstallCount > 0) {
            return einstallCount;
        }

        return 0;
    }

    getStandortLabel(standort: GruppeStandort, index: number): string {
        const stall = standort?.stall?.trim();
        if (stall) {
            return stall;
        }
        if (index === 0 && this.gruppe?.stall?.trim()) {
            return this.gruppe.stall.trim();
        }
        return '---';
    }

    /**
     * Total Einstall kg: SUM from GST rows (includes animals added later via Einzeltiergewicht).
     * Falls back to gs.einstallgewicht from standort wechsel when no per-tier weights exist yet.
     */
    getStandortEinstallgewichtTotal(standort: GruppeStandort, index?: number): number {
        const gstSum = Number(standort?.einstallgewicht_gesamt);
        if (!isNaN(gstSum) && gstSum > 0) {
            return gstSum;
        }
        return this.getStandortEinstallgewichtGesamt(standort);
    }

    /** gs.einstallgewicht = Gesamtgewicht entered at standort wechsel (snapshot, may be stale). */
    private getStandortEinstallgewichtGesamt(standort: GruppeStandort): number {
        const directWeight = Number(standort?.einstallgewicht);
        if (!isNaN(directWeight) && directWeight > 0) {
            return directWeight;
        }
        return 0;
    }

    /**
     * Abgang on standort N: SUM of GST abgang rows on N, else Gesamtgewicht from standort wechsel on N+1
     * (gs.einstallgewicht only — not the live GST einstall sum, which can include animals added later on N+1).
     */
    getStandortAbgangsgewichtTotal(standort: GruppeStandort, index?: number): number {
        const gstSum = Number(standort?.abgangsgewicht_gesamt);
        if (!isNaN(gstSum) && gstSum > 0) {
            return gstSum;
        }

        return this.getAbgangGesamtFromNextStandort(index);
    }

    getStandortAbgangTierCount(standort: GruppeStandort, index?: number): number {
        const abgangCount = Number(standort?.abgangsgewicht_avg?.anzahl);
        if (!isNaN(abgangCount) && abgangCount > 0) {
            return abgangCount;
        }

        if (index !== undefined && this.getAbgangGesamtFromNextStandort(index) > 0) {
            const next = this.standorte?.[index + 1];
            const nextCount = Number(next?.anzahl_tiere);
            if (!isNaN(nextCount) && nextCount > 0) {
                return nextCount;
            }
        }

        return 0;
    }

    /** Standort-wechsel snapshot on next standort (excludes later Einzeltiergewicht on N+1). */
    private getAbgangGesamtFromNextStandort(index?: number): number {
        if (index === undefined || index < 0 || index >= this.standorte.length - 1) {
            return 0;
        }
        return this.getStandortEinstallgewichtGesamt(this.standorte[index + 1]);
    }

    getStandortEinstallTierCount(standort: GruppeStandort): number {
        const einstallCount = Number(standort?.einstallgewicht_avg?.anzahl);
        if (!isNaN(einstallCount) && einstallCount > 0) {
            return einstallCount;
        }
        return 0;
    }

    getStandortTageszunahmen(standort: GruppeStandort, index?: number): number {
        const wert = Number(standort?.tageszunahmen_avg?.wert);
        if (!isNaN(wert) && wert > 0) {
            return wert;
        }
        return 0;
    }

    toggleStandortDetails(index: number): void {
        this.expandedStandortIndex = this.expandedStandortIndex === index ? null : index;
    }

    isStandortExpanded(index: number): boolean {
        return this.expandedStandortIndex === index;
    }

    get firstStandort(): GruppeStandort | null {
        return this.standorte?.length ? this.standorte[0] : null;
    }

    get lastStandort(): GruppeStandort | null {
        return this.standorte?.length ? this.standorte[this.standorte.length - 1] : null;
    }
}
